'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { AgentLineChat } from '@/app/components/icons/AgentLineChat';
import { Loader } from '@/app/components/ui/loader';
import { getLocalDayDiff } from '@/app/utils';
import { useClawSessions } from '../../_hook/use-claw-sessions';
import type { ClawV1Session } from '../../types';
import { NavButton } from './NavButton';
import { NavSkeleton } from './NavSkeleton';
import { shouldShowSessionLoadingDot, shouldShowSessionUnreadDot } from './nav-session-helpers';

interface NavSessionListProps {
  pathname: string;
  activeSessionId: string | null;
  isClawCreating: boolean;
}

interface SessionGroup {
  id: string;
  label: string;
  sessions: ClawV1Session[];
  order: number;
  latestTime: number;
}

const formatMonthLabel = (date: Date) => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}`;
};

const getSessionDate = (session: ClawV1Session) => {
  const timestamp =
    session.last_message_at && session.last_message_at !== '0001-01-01T00:00:00Z'
      ? session.last_message_at
      : (session.updated_at ?? session.created_at);
  if (!timestamp) return null;

  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getSessionGroupMeta = (
  date: Date | null,
  now: Date,
  labels: {
    today: string;
    yesterday: string;
    sevenDays: string;
    thirtyDays: string;
  },
) => {
  if (!date) {
    return {
      id: 'thirty-days',
      label: labels.thirtyDays,
      order: 3,
    };
  }

  const diffDays = getLocalDayDiff(date, now);

  if (diffDays <= 0) {
    return { id: 'today', label: labels.today, order: 0 };
  }
  if (diffDays === 1) {
    return { id: 'yesterday', label: labels.yesterday, order: 1 };
  }
  if (diffDays <= 7) {
    return { id: 'seven-days', label: labels.sevenDays, order: 2 };
  }
  if (diffDays <= 30) {
    return { id: 'thirty-days', label: labels.thirtyDays, order: 3 };
  }

  const label = formatMonthLabel(date);
  return { id: `month-${label}`, label, order: 4 };
};

const groupSessionsByTime = (
  sessions: ClawV1Session[],
  labels: {
    today: string;
    yesterday: string;
    sevenDays: string;
    thirtyDays: string;
  },
): SessionGroup[] => {
  const groupMap = new Map<string, SessionGroup>();
  const now = new Date();

  sessions.forEach((session) => {
    const date = getSessionDate(session);
    const latestTime = date?.getTime() ?? 0;
    const groupMeta = getSessionGroupMeta(date, now, labels);
    const group = groupMap.get(groupMeta.id);

    if (group) {
      group.sessions.push(session);
      group.latestTime = Math.max(group.latestTime, latestTime);
      return;
    }

    groupMap.set(groupMeta.id, {
      ...groupMeta,
      sessions: [session],
      latestTime,
    });
  });

  return Array.from(groupMap.values()).sort((prev, next) => {
    if (prev.order !== next.order) return prev.order - next.order;
    return next.latestTime - prev.latestTime;
  });
};

export const NavSessionList = ({ pathname, activeSessionId, isClawCreating }: NavSessionListProps) => {
  const t = useTranslations();
  const { sessions, isLoading, isInitialLoading, containerRef } = useClawSessions();
  const showSkeleton = isClawCreating || isInitialLoading;
  const showLoader = !isClawCreating && isLoading && sessions.length > 0;
  const sessionGroups = useMemo(
    () =>
      groupSessionsByTime(sessions, {
        today: t('claw.nav.sessionGroups.today'),
        yesterday: t('claw.nav.sessionGroups.yesterday'),
        sevenDays: t('claw.nav.sessionGroups.sevenDays'),
        thirtyDays: t('claw.nav.sessionGroups.thirtyDays'),
      }),
    [sessions, t],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1">
      <div ref={containerRef} className="flex flex-1 flex-col gap-1 overflow-y-auto">
        {showSkeleton ? (
          <NavSkeleton />
        ) : (
          sessionGroups.map((group) => (
            <section key={group.id} className="flex flex-col">
              <div className="flex h-7 items-center rounded-12 px-3 py-1 light:text-system-text04 text-system-white24 text-xs leading-[1.4]">
                {group.label}
              </div>
              {group.sessions.map((item) => (
                <NavButton
                  key={item.id}
                  item={{
                    id: item.id ?? '',
                    type: 'session',
                    path: `/claw/${item.id}`,
                    icon: AgentLineChat,
                    label: item.name ?? '',
                    mode: item.mode,
                    hasUnread: shouldShowSessionUnreadDot({ activeSessionId, session: item }),
                    isLoadingUnread: shouldShowSessionLoadingDot({ activeSessionId, session: item }),
                  }}
                  pathname={pathname}
                  activeSessionId={activeSessionId}
                />
              ))}
            </section>
          ))
        )}
        {showLoader && (
          <div className="flex h-6 items-center justify-center">
            <Loader className="size-4" />
          </div>
        )}
      </div>
    </div>
  );
};
